import { useUserPreferenceDefaults } from './useUserPreferenceDefaults';
import { useApp } from '@/contexts/AppContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import * as formatting from '../lib/formatting';
import { DisplayPrecision, ROLE_TEMPLATES } from '../types/permissions';
import { getDemoRoles } from '@/utils/demoRolePersistence';
import { getRoleTemplateId } from '@/lib/permissions';

/**
 * Hook that provides formatting functions based on user preference defaults.
 * Uses the configured date and number formats from admin settings.
 * Also handles display precision based on role settings and user overrides.
 */
export function useFormatting() {
  const { prefs } = useUserPreferenceDefaults();
  const { dateFormat, numberFormat } = prefs;
  const { currentUser, mode } = useApp();
  const { getUserPrecisionOverride } = useUserPreferences();

  // Get display precision from current user's role
  const roleId = getRoleTemplateId(currentUser.forecasterRole);
  const roles = mode === 'demo' ? getDemoRoles() : ROLE_TEMPLATES;
  const role = roles.find(r => r.id === roleId);

  // Check for user override if role allows it
  const userOverride = role?.allowUserOverride ? getUserPrecisionOverride(currentUser.id) : null;
  const displayPrecision: DisplayPrecision = userOverride || role?.displayPrecision || 'full';
  


  /**
   * Format a date according to user preference
   * @param date - Date to format
   * @param options - Optional formatting options (day, month, year, etc.)
   * @returns Formatted date string
   */
  const formatDate = (
    date: Date,
    options?: {
      day?: 'numeric' | '2-digit';
      month?: 'numeric' | '2-digit' | 'short' | 'long';
      year?: 'numeric' | '2-digit';
      hour?: '2-digit';
      minute?: '2-digit';
    }
  ): string => {
    return formatting.formatDate(date, dateFormat, options);
  };

  /**
   * Format currency according to user preference and display precision.
   *
   * @param amount  - Amount to format
   * @param precisionOverride - Pass 'full' | 'rounded' | 'abbreviated' | 'millions' to
   *   override the persona-defined precision for this call.  Use 'full' on all
   *   operational / working screens (tables, modals, forms) per the Precision
   *   Display Principle in AGENTS.md.  When omitted the resolved persona precision
   *   is used (correct for dashboard cards and KPI summaries).
   * @param options - Optional Intl formatting options
   * @returns Formatted currency string
   */
  const formatCurrency = (
    amount: number,
    precisionOverride?: DisplayPrecision | null,
    options?: {
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
      compact?: boolean;
    }
  ): string => {
    const currency = 'GBP';
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    
    // Get currency symbol from system settings (default to £)
    const currencySymbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
    
    // Use precisionOverride when explicitly passed, otherwise fall back to persona-resolved precision
    const effectivePrecision: DisplayPrecision = precisionOverride ?? displayPrecision;

    let formattedValue: string;

    switch (effectivePrecision) {
      case 'rounded':
        // Round to nearest thousand, no decimals
        const roundedAmount = Math.round(absAmount / 1000) * 1000;
        formattedValue = formatting.formatCurrency(roundedAmount, currency, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }, numberFormat);
        break;
        
      case 'abbreviated':
        // Use M for values >= £1,000,000, K for >= £1,000
        if (absAmount >= 1000000) {
          const mValue = absAmount / 1000000;
          formattedValue = `${currencySymbol}${mValue.toFixed(1)}M`;
        } else if (absAmount >= 1000) {
          const kValue = absAmount / 1000;
          formattedValue = `${currencySymbol}${kValue.toFixed(kValue >= 100 ? 0 : 1)}K`;
        } else {
          formattedValue = formatting.formatCurrency(absAmount, currency, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }, numberFormat);
        }
        break;
        
      case 'millions':
        // Use M for values >= £100,000, otherwise fall back to K
        if (absAmount >= 100000) {
          const mValue = absAmount / 1000000;
          formattedValue = `${currencySymbol}${mValue.toFixed(2)}M`;
        } else if (absAmount >= 1000) {
          const kValue = absAmount / 1000;
          formattedValue = `${currencySymbol}${kValue.toFixed(kValue >= 100 ? 0 : 1)}K`;
        } else {
          formattedValue = formatting.formatCurrency(absAmount, currency, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }, numberFormat);
        }
        break;
        
      case 'full':
      default:
        // Full precision with decimals — always show pence
        formattedValue = formatting.formatCurrency(absAmount, currency, {
          minimumFractionDigits: options?.minimumFractionDigits ?? 2,
          maximumFractionDigits: options?.maximumFractionDigits ?? 2,
          ...options,
        }, numberFormat);
        break;
    }
    
    // Add negative sign before currency symbol if needed
    return isNegative ? `-${formattedValue}` : formattedValue;
  };

  /**
   * Format a number according to user preference (without currency symbol)
   * @param amount - Number to format
   * @param options - Optional formatting options
   * @returns Formatted number string
   */
  const formatNumber = (
    amount: number,
    options?: {
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ): string => {
    return formatting.formatNumber(amount, numberFormat, options);
  };

  return { formatDate, formatCurrency, formatNumber, dateFormat, numberFormat };
}
